// پوستهٔ مشترک فرم‌های تعریف پایه: گرید + جدید/ویرایش/حذف + تغییر موارد استفاده
// به‌صورت صفحهٔ کامل (ListPage) یا پنجرهٔ روی صفحهٔ دیگر (asDialog).
import React, { useState, useEffect, useCallback, useRef } from 'react';
import ListPage, { ui } from '../../components/ListPage';
import { usePagedList, CountBadge, MoreButton } from '../../components/paging';
import { ExcelButton } from '../../components/excel';
import WarningDialog from '../../components/WarningDialog';
import { useFormButtons } from '../../utils/vbPerms';
import { basicApi, DataTable, Modal, MsgBox, ChangeUsesDialog, AfterMoveAsk, btnDisabled } from './basicShared';

export default function CrudView({
  user, onBack, asDialog = false, onClose, onPick,
  form: formName, buttons = [], title, subtitle = 'تدارکات — سامانه ارجان', unit = 'ردیف',
  api = basicApi, path, listQuery = () => '', initialOpts = {}, filters,
  cols, excelCols, empty, newForm, toForm, renderFields, prepare = f => f, confirmSave,
  changeBtn, extraButtons, extraModalButtons, modalWidth = 560, rowStyle, onCellContext, onRowSelected,
  footerExtra, reloadKey, children, updateMethod = 'PUT', deleteMethod = 'DELETE',
}) {
  const allBtns = ['BTNNEW', 'BTNEDIT', 'BTNDELETE', 'BTNOK', ...(changeBtn ? [changeBtn, 'BTNMOVE'] : []), ...buttons];
  const fb = useFormButtons(formName, allBtns);
  const [opts, setOpts] = useState({ search: '', ...initialOpts });
  const [meta, setMeta] = useState({});
  const L = usePagedList((page, o) => api(`${path}/?page=${page}&search=${encodeURIComponent(o.search || '')}${listQuery(o)}`)
    .then(j => { if (j && j.ok && page === 1) setMeta(j); return j; }));
  const [sel, setSel] = useState(null);
  const [edit, setEdit] = useState(null);            // {mode:'new'|'edit', ...fields}
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState([]);
  const [ask, setAsk] = useState(null);              // {lines, yes, no, title, yesLabel, noLabel}
  const [changing, setChanging] = useState(false);
  const pendingStep = useRef(null);
  const optsRef = useRef(opts);
  optsRef.current = opts;

  const reload = useCallback((o) => { setSel(null); return L.reload(o || optsRef.current); }, [L]);
  useEffect(() => { if (fb.loaded && fb.open) reload(); }, [fb.loaded, fb.open, reloadKey]);   // بار اول

  const set = (k, v) => setEdit(f => ({ ...f, [k]: v }));
  const setMany = obj => setEdit(f => ({ ...f, ...obj }));

  const openNew = async () => {
    if (!fb.can('BTNNEW')) return;
    setErr('');
    const base = newForm ? await newForm() : { ...empty };
    if (base) setEdit({ mode: 'new', ...base });
  };
  const openEdit = (r = sel) => {
    if (!fb.can('BTNEDIT') || !r) return;
    setErr(''); setEdit({ mode: 'edit', ...(toForm ? toForm(r) : r) });
  };

  const doSave = async (f, confirmed) => {
    setSaving(true); setErr('');
    const body = JSON.stringify({ ...prepare(f), ...(confirmed ? { confirmed: 1 } : {}) });
    const j = f.mode === 'new'
      ? await api(`${path}/create/`, { method: 'POST', body })
      : await api(`${path}/${sel.id}/update/`, { method: updateMethod, body });
    setSaving(false);
    if (j.ok) { setEdit(null); await reload(); return; }
    if (j.need_confirm) {
      const confirms = j.confirms || [];
      const step = i => {
        if (i >= confirms.length) { setAsk(null); doSave(f, true); return; }
        setAsk({ title: 'هشدار', lines: confirms[i].split('\n'), yesLabel: 'بلی', noLabel: 'خیر',
          yes: () => step(i + 1), no: () => setAsk(null) });
      };
      if ((j.notices || []).length) setMsg([...j.notices, '__then__']);
      else step(0);
      pendingStep.current = () => step(0);
      return;
    }
    setErr(j.msg || '');
  };
  const closeMsg = () => {
    const hadThen = msg.includes('__then__');
    setMsg([]);
    if (hadThen && pendingStep.current) { const s = pendingStep.current; pendingStep.current = null; s(); }
  };

  const save = () => {
    if (!fb.can('BTNOK')) return;
    const lines = confirmSave ? confirmSave(edit, sel) : null;
    if (lines && lines.length) {
      setAsk({ title: 'انتخاب کنید', lines, yesLabel: 'بلی', noLabel: 'خیر',
        yes: () => { setAsk(null); doSave(edit, false); }, no: () => setAsk(null) });
    } else doSave(edit, false);
  };

  const doDelete = async (r) => {
    const j = await api(`${path}/${r.id}/delete/`, { method: deleteMethod });
    if (j.ok) reload(); else setMsg([j.msg || 'خطا']);
  };
  const askDelete = () => {
    if (!fb.can('BTNDELETE') || !sel) return;
    const r = sel;
    setAsk({ title: 'هشدار', lines: ['آيا از حذف اين رکورد مطمئن هستيد؟'], yesLabel: 'بلی', noLabel: 'خیر',
      yes: () => { setAsk(null); doDelete(r); }, no: () => setAsk(null) });
  };

  const select = r => { setSel(r); if (onRowSelected) onRowSelected(r, { api, reload, setAsk, setMsg, fb }); };
  const dbl = r => { if (onPick) onPick(r); };

  const colsArr = typeof cols === 'function' ? cols(meta) : cols;
  const ctx = { sel, fb, reload, setMsg, setAsk, meta, opts, setOpts, api, data: L.data };
  const has = !!sel;
  const toolbar = (
    <>
      {fb.open && (
        <div style={ui.toolBtns}>
          {fb.can('BTNNEW') && <button onClick={openNew} style={ui.btnNew}>＋ جدید</button>}
          {fb.can('BTNEDIT') && <button onClick={() => openEdit()} disabled={!has} style={btnDisabled(ui.btnEdit, has)}>✎ ویرایش</button>}
          {fb.can('BTNDELETE') && <button onClick={askDelete} disabled={!has} style={btnDisabled(ui.btnDel, has)}>✕ حذف</button>}
          {changeBtn && fb.can(changeBtn) && <button onClick={() => has && setChanging(true)} disabled={!has}
            style={btnDisabled(ui.btnItems, has)}>تغییر موارد استفاده</button>}
          {extraButtons && extraButtons(ctx)}
          {onPick && <button onClick={() => sel && onPick(sel)} disabled={!has} style={btnDisabled(ui.btnSave, has)}>انتخاب</button>}
        </div>
      )}
      {fb.open && (
        <>
          <input value={opts.search} onChange={e => setOpts(o => ({ ...o, search: e.target.value }))}
            onKeyDown={e => e.key === 'Enter' && reload()} placeholder="جستجو..."
            style={{ ...ui.searchInput, flex: 1, minWidth: '160px' }} />
          {filters && filters(ctx)}
          <button onClick={() => reload()} style={{ ...ui.btnEdit, cursor: 'pointer' }}>جستجو</button>
          <ExcelButton title={title} columns={(excelCols || colsArr).map(c => ({ key: c.key, label: c.label, num: c.num }))} rows={L.data} />
          <CountBadge shown={L.data.length} total={L.pager.total} loading={L.loading} unit={unit} />
        </>
      )}
    </>
  );

  const grid = (
    <DataTable cols={colsArr} rows={L.data} selectedId={sel?.id} onSelect={select}
      onDouble={onPick ? dbl : (r => { select(r); })} rowStyle={rowStyle}
      onCellContext={onCellContext ? (r, k) => onCellContext(r, k, ctx) : undefined} />
  );

  const overlays = (
    <>
      {edit && (
        <Modal title={edit.mode === 'new' ? `＋ ${title} — جدید` : `✎ ${title} — ویرایش`} onClose={() => setEdit(null)}
          width={modalWidth} z={1200}
          footer={<>
            {extraModalButtons && extraModalButtons({ ...ctx, edit })}
            <button onClick={() => setEdit(null)} style={ui.btnCancel}>لغو</button>
            {fb.can('BTNOK') && <button onClick={save} disabled={saving} style={ui.btnSave}>{saving ? '⏳' : 'ثبت'}</button>}
          </>}>
          {err && <div style={ui.errBox}>⚠️ {err}</div>}
          {renderFields({ f: edit, set, setMany, meta, sel })}
        </Modal>
      )}
      {changing && sel && (
        <ChangeUsesDialog api={api} base={path} rec={sel} canMove={fb.can('BTNMOVE')}
          onClose={() => setChanging(false)} onMessage={m => setMsg([m])}
          onMoved={() => {
            setChanging(false);
            const r = sel;
            setAsk({ custom: 'after', yes: () => { setAsk(null); if (fb.can('BTNDELETE')) doDelete(r); else reload(); },
              no: () => { setAsk(null); reload(); } });
          }} />
      )}
      {ask && ask.custom === 'after' && <AfterMoveAsk onYes={ask.yes} onNo={ask.no} />}
      {ask && !ask.custom && <WarningDialog title={ask.title} lines={ask.lines} confirmLabel={ask.yesLabel}
        cancelLabel={ask.noLabel} onConfirm={ask.yes} onCancel={ask.no} />}
      <MsgBox lines={msg.filter(x => x !== '__then__')} onClose={closeMsg} />
      {children && children(ctx)}
    </>
  );

  if (asDialog) {
    return (
      <Modal title={title} onClose={onClose} width={900} z={1100}
        footer={<><MoreButton pager={L.pager} loading={L.loading} onMore={L.more} unit={unit} />
          {footerExtra}<button onClick={onClose} style={ui.btnCancel}>بستن</button></>}>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: '10px' }}>{toolbar}</div>
        {L.error && <div style={ui.errBox}>⚠️ {L.error}</div>}
        <div style={{ maxHeight: '55vh', overflow: 'auto', border: '1px solid #eee', borderRadius: '8px' }}>{grid}</div>
        {overlays}
      </Modal>
    );
  }
  return (
    <>
      <ListPage user={user} onBack={onBack} title={title} subtitle={subtitle} toolbar={toolbar}
        error={L.error} pager={L.pager} onMore={L.more} loading={L.loading} unit={unit}>
        {grid}
      </ListPage>
      {overlays}
    </>
  );
}
